/**
 * ⭐⭐ **O TEXTO COLADO DO CHAT VIRA BLOCOS — 23/09/2026.**
 *
 * > *"Nos campos que eu colo o retorno dos prompts, precisamos ter a
 * > possibilidade de editar com as mesmas ferramentas dos campos
 * > '+ Acrescentar campo de escrita' (negrito, marcadores e etc)."* — Richard
 *
 * O campo do passo 2 é texto puro: o que volta da IA entra cru, e a faixa de
 * formatação não o alcança. Convertendo para **blocos e trechos**, ele vira um
 * campo de escrita como qualquer outro — com negrito, marcador, tabela e
 * imagem.
 *
 * ⛔ **E resolve um defeito visível no documento:** o `**` do chat saía
 * impresso. No PDF do caso 2026-003 aparecia
 * `**MAUSER SECURITY & LABELS EIRELI**`, com os asteriscos, num parágrafo que
 * vai ao cliente.
 *
 * ⚠️ **Converte o que o chat realmente escreve, e nada mais:** negrito,
 * itálico e marcador. Título, tabela e link pedem decisão de quem escreve, e
 * quem escreve tem a faixa.
 */
import type { Bloco, Trecho } from './api';

/** `**assim**` e `*assim*` viram trechos marcados. */
function trechosDe(linha: string): Trecho[] {
  const saida: Trecho[] = [];
  /* ⚠️ O negrito vem ANTES do itálico: `**x**` casaria como dois itálicos. */
  const partes = linha.split(/(\*\*[^*]+\*\*|\*[^*\n]+\*|__[^_]+__|_[^_\n]+_)/g);
  for (const parte of partes) {
    if (!parte) continue;
    const negrito = /^(\*\*|__)(.+)(\*\*|__)$/s.exec(parte);
    if (negrito) {
      saida.push({ t: negrito[2] ?? '', n: true });
      continue;
    }
    const italico = /^(\*|_)(.+)(\*|_)$/s.exec(parte);
    if (italico) {
      saida.push({ t: italico[2] ?? '', i: true });
      continue;
    }
    saida.push({ t: parte });
  }
  return saida.length > 0 ? saida : [{ t: '' }];
}

const EH_ITEM = /^\s*([-–—*•]|\d{1,2}[.)])\s+/;

/**
 * Lê o texto colado e devolve os blocos.
 *
 * ⚠️ **Linha em branco separa parágrafo**, como no editor. Linhas seguidas que
 * começam por marcador viram **uma** lista — não uma lista por linha.
 */
export function blocosDoTextoColado(texto: string): Bloco[] {
  const blocos: Bloco[] = [];
  let lista: Trecho[][] | null = null;
  let ordenada = false;

  const fecharLista = (): void => {
    if (lista && lista.length > 0) {
      blocos.push(ordenada ? { especie: 'lista', ordenada: true, itens: lista } : { especie: 'lista', itens: lista });
    }
    lista = null;
  };

  for (const bruta of texto.replace(/\r\n/g, '\n').split('\n')) {
    const linha = bruta.trimEnd();
    if (!linha.trim()) {
      fecharLista();
      continue;
    }
    const item = EH_ITEM.exec(linha);
    if (item) {
      const numerada = /^\s*\d/.test(item[1] ?? '');
      if (lista === null) {
        lista = [];
        ordenada = numerada;
      }
      lista.push(trechosDe(linha.replace(EH_ITEM, '')));
      continue;
    }
    fecharLista();
    blocos.push({ especie: 'paragrafo', trechos: trechosDe(linha) });
  }
  fecharLista();
  /* ⚠️ Campo vazio é intenção, não texto: devolve um parágrafo em branco. */
  return blocos.length > 0 ? blocos : [{ especie: 'paragrafo', trechos: [{ t: '' }] }];
}

/** Escapa o que vai virar HTML. */
const esc = (t: string): string =>
  t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * ⭐ **O mesmo texto, em HTML para colar no editor.**
 *
 * ⚠️ **Sai do MESMO leitor** (`blocosDoTextoColado`): dois interpretadores da
 * marcação do chat divergiriam na primeira correção feita só num deles.
 *
 * ⛔ **Só `<b>`, `<i>`, `<ul>`, `<ol>` e `<div>`** — as marcas que o editor já
 * sabe ler de volta. Qualquer outra viraria lixo que `lerDoEditor` descarta.
 */
export function htmlDoTextoColado(texto: string): string {
  const emHtml = (trechos: Trecho[]): string =>
    trechos
      .map((t) => {
        const corpo = esc(t.t);
        if (t.n) return `<b>${corpo}</b>`;
        if (t.i) return `<i>${corpo}</i>`;
        return corpo;
      })
      .join('');

  return blocosDoTextoColado(texto)
    .map((b) => {
      if (b.especie === 'paragrafo') return `<div>${emHtml(b.trechos) || '<br>'}</div>`;
      if (b.especie === 'lista') {
        const itens = b.itens.map((i) => `<li>${emHtml(i)}</li>`).join('');
        return b.ordenada ? `<ol>${itens}</ol>` : `<ul>${itens}</ul>`;
      }
      return '';
    })
    .join('');
}

/**
 * O texto colado traz marcação do chat que vale converter?
 *
 * ⚠️ **Sem isto, TODA colagem passaria pelo conversor** — inclusive a que vem
 * do Word com formatação própria, que o navegador já entrega em HTML e o
 * editor já sabe ler. Converter ali seria trocar formatação real por texto.
 */
export function pareceMarcacaoDeChat(texto: string): boolean {
  return /(\*\*[^*\n]+\*\*)|(^\s*[-–—*•]\s+.+$)|(^\s*\d{1,2}[.)]\s+.+$)/m.test(texto);
}
